import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowUpDown } from 'lucide-react';
import type { DashboardStudentItem } from '@/types/dashboard';

interface SessionFiltersProps {
  status: string;
  studentId: string;
  source: string;
  dateFrom: string;
  dateTo: string;
  sortOrder: string;
  students?: DashboardStudentItem[];
  hideStudent?: boolean;
  onStatusChange: (value: string | null) => void;
  onStudentChange: (value: string | null) => void;
  onSourceChange: (value: string | null) => void;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onSortOrderToggle: () => void;
}

export function SessionFilters({
  status,
  studentId,
  source,
  dateFrom,
  dateTo,
  sortOrder,
  students,
  hideStudent = false,
  onStatusChange,
  onStudentChange,
  onSourceChange,
  onDateFromChange,
  onDateToChange,
  onSortOrderToggle,
}: SessionFiltersProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Select value={status} onValueChange={onStatusChange}>
        <SelectTrigger className="w-[150px]">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="done">Completed</SelectItem>
          <SelectItem value="skipped">Skipped</SelectItem>
          <SelectItem value="pending">Pending</SelectItem>
          <SelectItem value="reminded">Reminded</SelectItem>
        </SelectContent>
      </Select>

      <Select value={source} onValueChange={onSourceChange}>
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Source" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All sources</SelectItem>
          <SelectItem value="assignment">Assignment</SelectItem>
          <SelectItem value="ai">AI practice</SelectItem>
          <SelectItem value="seed">Seed</SelectItem>
        </SelectContent>
      </Select>

      {!hideStudent && (
        <Select value={studentId} onValueChange={onStudentChange}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="All students" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All students</SelectItem>
            {students?.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {[s.first_name, s.last_name].filter(Boolean).join(' ') ||
                  s.username ||
                  s.email ||
                  s.id.slice(0, 8)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <Input
        type="date"
        value={dateFrom}
        onChange={(e) => onDateFromChange(e.target.value)}
        className="w-[160px]"
        aria-label="From date"
      />
      <Input
        type="date"
        value={dateTo}
        onChange={(e) => onDateToChange(e.target.value)}
        className="w-[160px]"
        aria-label="To date"
      />

      <Button variant="outline" size="icon" onClick={onSortOrderToggle}>
        <ArrowUpDown className="h-4 w-4" />
        <span className="sr-only">{sortOrder === 'asc' ? 'Ascending' : 'Descending'}</span>
      </Button>
    </div>
  );
}
